import { TFunction } from 'i18next';
import { EvacZone } from 'shared/types';
import { formatNameList } from 'shared/utils';

// Represents a group of related zones in a pending operation
type PendingZoneGroup = {
  zones: EvacZone[];
  releaseUids: string[];
};

// Gets all zones in a group starting from a zone with status
const getZoneGroup = (
  statusedZone: EvacZone,
  pendingZones: EvacZone[],
): EvacZone[] => {
  const neededUids = new Set([statusedZone.uidV2]);

  // Add children and their parents
  statusedZone.childUids?.forEach((childUid) => {
    neededUids.add(childUid);
    const child = pendingZones.find((z) => z.uidV2 === childUid);
    child?.parentUids?.forEach((parentUid) => neededUids.add(parentUid));
  });

  // Collect matching zones
  return pendingZones.filter((zone) => neededUids.has(zone.uidV2));
};

/**
 * Groups all pending zones into related operation groups
 * @param zones - All zones from the API
 * @param zoneUidsWithStatus - uidV2s of zones that currently have evacuation status
 * @returns Array of pending zone groups
 */
export const getPendingZoneGroups = (
  zones: EvacZone[],
  zoneUidsWithStatus: string[],
): PendingZoneGroup[] => {
  const pendingZones = zones.filter((zone) => zone.isPendingReview);
  const activeZonesWithStatus = pendingZones.filter(
    (zone) => zone.isActive && zoneUidsWithStatus.includes(zone.uidV2),
  );

  const groups: PendingZoneGroup[] = [];
  const processedUids = new Set<string>();

  for (const statusedZone of activeZonesWithStatus) {
    if (!processedUids.has(statusedZone.uidV2)) {
      const zoneGroup = getZoneGroup(statusedZone, pendingZones);

      // Get uidV2s from this group that have evacuation status
      const releaseUids = zoneGroup
        .filter((zone) => zoneUidsWithStatus.includes(zone.uidV2))
        .map((zone) => zone.uidV2);

      groups.push({ zones: zoneGroup, releaseUids });

      // Mark all zones in this group as processed
      zoneGroup.forEach((zone) => processedUids.add(zone.uidV2));
    }
  }

  return groups;
};

export const generatePendingZoneNames = (
  t: TFunction,
  zoneGroup: PendingZoneGroup,
): { activeNames: string; resultingNames: string } => {
  const activeZoneNames = zoneGroup.zones
    .filter((zone) => zone.isActive)
    .map((z) => z.displayName)
    .sort();

  // Get names of zones that will exist after the operation completes
  const resultingZoneNames = zoneGroup.zones
    .filter((zone) => zone.childUids?.includes(zone.uidV2) || !zone.isActive)
    .map((zone) => zone.displayName)
    .sort();

  const formattedActiveNames = formatNameList(t, activeZoneNames);
  const formattedResultingNames = formatNameList(t, resultingZoneNames);

  return {
    activeNames: formattedActiveNames,
    resultingNames: formattedResultingNames,
  };
};
